// ISO 3166-1 alpha-2 country codes. Names are resolved via Intl.DisplayNames
// so we don't have to hand-maintain 190+ country names.
const COUNTRY_CODES = [
  "US", "CA", "GB", "AU", "NZ", "IE", "IN", "PK", "BD", "LK", "NP",
  "DE", "FR", "IT", "ES", "PT", "NL", "BE", "CH", "AT", "SE", "NO",
  "DK", "FI", "PL", "CZ", "GR", "HU", "RO", "BG", "HR", "SK", "SI",
  "UA", "RU", "TR",
  "AE", "SA", "QA", "KW", "BH", "OM", "IL", "EG", "ZA", "NG", "KE",
  "GH", "MA", "TN",
  "CN", "JP", "KR", "SG", "MY", "TH", "VN", "PH", "ID", "HK", "TW",
  "MX", "BR", "AR", "CL", "CO", "PE",
];

// Calling codes for the phone number mask ("+<code> 000 000 0000"). Keyed by
// the same ISO codes as COUNTRY_CODES above.
const COUNTRY_CALLING_CODES = {
  US: "1", CA: "1", GB: "44", AU: "61", NZ: "64", IE: "353", IN: "91",
  PK: "92", BD: "880", LK: "94", NP: "977",
  DE: "49", FR: "33", IT: "39", ES: "34", PT: "351", NL: "31", BE: "32",
  CH: "41", AT: "43", SE: "46", NO: "47", DK: "45", FI: "358", PL: "48",
  CZ: "420", GR: "30", HU: "36", RO: "40", BG: "359", HR: "385", SK: "421",
  SI: "386", UA: "380", RU: "7", TR: "90",
  AE: "971", SA: "966", QA: "974", KW: "965", BH: "973", OM: "968",
  IL: "972", EG: "20", ZA: "27", NG: "234", KE: "254", GH: "233", MA: "212",
  TN: "216",
  CN: "86", JP: "81", KR: "82", SG: "65", MY: "60", TH: "66", VN: "84",
  PH: "63", ID: "62", HK: "852", TW: "886",
  MX: "52", BR: "55", AR: "54", CL: "56", CO: "57", PE: "51",
};

const countryDisplayNames = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch (error) {
    return null;
  }
})();

export const COUNTRIES = COUNTRY_CODES
  .map((code) => ({
    code,
    name: countryDisplayNames?.of(code) || code,
    callingCode: COUNTRY_CALLING_CODES[code] || "",
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const FALLBACK_TIMEZONES = [
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Sao_Paulo", "Europe/London", "Europe/Paris", "Europe/Berlin",
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Asia/Shanghai",
  "Australia/Sydney", "Pacific/Auckland", "UTC",
];

export const TIMEZONES = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch (error) {
    return FALLBACK_TIMEZONES;
  }
})();

// "GMT+05:30" for a timezone, right now (offsets shift with daylight saving,
// so this is only ever computed against the current date).
function getGmtOffsetLabel(timeZone) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" }).formatToParts(new Date());
    const offset = parts.find((part) => part.type === "timeZoneName")?.value;
    return offset && offset !== "GMT" ? offset : "GMT+00:00";
  } catch (error) {
    return "GMT+00:00";
  }
}

function getGmtOffsetMinutes(offsetLabel) {
  const match = offsetLabel.match(/GMT([+-])(\d{2}):(\d{2})/);
  if (!match) return 0;
  const sign = match[1] === "-" ? -1 : 1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

// {value, label} options for the time zone dropdown, e.g.
// "(GMT-04:00) America/New_York" -- sorted by offset so nearby zones group
// together instead of being scattered alphabetically.
export const TIMEZONE_OPTIONS = TIMEZONES
  .map((timeZone) => {
    const offsetLabel = getGmtOffsetLabel(timeZone);
    return {
      value: timeZone,
      label: `(${offsetLabel}) ${timeZone}`,
      offsetMinutes: getGmtOffsetMinutes(offsetLabel),
    };
  })
  .sort((a, b) => a.offsetMinutes - b.offsetMinutes || a.value.localeCompare(b.value));

// Mirrors the color keys the backend stores in facility_theme.theme_config.
// display_name/description are shown in the theme editor UI.
export const DEFAULT_THEME_CONFIG = [
  { display_name: "Primary button", color_key: "primary", color_value: "231 48% 48%", description: "Buttons, links, focus rings" },
  { display_name: "Primary button : hover", color_key: "primary-hover", color_value: "231 45% 58%", description: "Hover and pressed states" },
  { display_name: "Primary button : faded", color_key: "primary-faded", color_value: "229 32% 78%", description: "Disabled buttons" },
  { display_name: "Active tab", color_key: "active-tab", color_value: "262 47% 55%", description: "Selected tab and nav item" },
  { display_name: "Light", color_key: "primary-light", color_value: "231 44% 76%", description: "Section tints" },
  { display_name: "Light-1", color_key: "primary-light-1", color_value: "231 44% 65%", description: "Secondary tints" },
  { display_name: "Icons", color_key: "svg", color_value: "259 63% 59%", description: "Icon tiles and glyphs" },
];

// The 4 colors shown as a quick summary on the Facility Detail > Branding tab.
// Kept in sync with the color_key values above -- there is no separate
// "surface" color in the backend schema.
export const BRANDING_SUMMARY_KEYS = ["primary", "active-tab", "primary-light", "svg"];

export const FACILITY_STATUS = {
  ALL: "all",
  ACTIVE: "active",
  INACTIVE: "inactive",
};

export const FACILITY_STATUS_TABS = [
  { value: FACILITY_STATUS.ALL, label: "All" },
  { value: FACILITY_STATUS.ACTIVE, label: "Active" },
  { value: FACILITY_STATUS.INACTIVE, label: "Inactive" },
];

export const MAX_FACILITY_FILES = parseInt(
  process.env.REACT_APP_MAX_FACILITY_FILES || "25",
  10
);
